import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useLayoutEffect,
  useEffect
} from "react";
import { useOutsideClicked } from "../../hooks";
import { createPortal } from "react-dom";
import { v4 as uuidv4 } from "uuid";

const Popover = ({
  children,
  content = "",
  defaultVisible = false,
  placement = "top", // preferred placement
  showOnHover = false,
  hoverInteractive = false,
  hoverCloseDelay = 200,
  theme = "white",
  hideArrow = false,
  onClick,
  className,
  onClickOutside,
  align = "center",
  width
}) => {

  // states
  const [isVisible, setIsVisible] = useState(defaultVisible)
  const [calculatedPlacement, setCalculatedPlacement] = useState(placement)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const [arrowPosition, setArrowPosition] = useState({})

  // ref
  const popoverRef = useRef(null)
  const popoverContentRef = useRef(null)
  const openTimerRef = useRef(null)
  const closeTimerRef = useRef(null)

  // memo
  const popoverId = useMemo(() => uuidv4(), [])
  const resolvedWidth = useMemo(() => ((typeof width === "number") ? `${width}px` : (width || undefined)), [width])

  const onClickOutsidePopover = useCallback((event) => {
    if (event?.target?.closest?.("[data-popover-keep-open]")) return

    setIsVisible(false)
    onClickOutside && onClickOutside()
  }, [onClickOutside])

  useOutsideClicked(popoverRef, onClickOutsidePopover)

  const onClickParent = useCallback((e) => {
    e.stopPropagation()
    onClick && onClick(!isVisible)
    if (!showOnHover) setIsVisible((prev) => !prev);
  },
    [isVisible, showOnHover, onClick]
  );

  const clearHoverTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  useEffect(() => clearHoverTimers, [clearHoverTimers])

  const onMouseEnter = useCallback(() => {
    if (!showOnHover) return

    clearHoverTimers()
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null
      setIsVisible(true)
    }, 100);
  }, [showOnHover, clearHoverTimers])

  const onMouseLeave = useCallback(() => {
    if (!showOnHover) return

    clearHoverTimers()
    if (!hoverInteractive) {
      setIsVisible(false)
      return
    }

    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      setIsVisible(false)
    }, hoverCloseDelay);
  }, [showOnHover, hoverInteractive, hoverCloseDelay, clearHoverTimers])

  const isHoverInteractive = (showOnHover && hoverInteractive)

  // common fn to reposition popover
  const repositionPopover = useCallback(() => {
    if (!isVisible) return

    const triggerEl = popoverRef.current
    const contentEl = popoverContentRef.current
    if (!triggerEl || !contentEl) return

    const triggerRect = triggerEl.getBoundingClientRect()
    const contentRect = contentEl.getBoundingClientRect()

    // computing base coords for placement
    const baseCoords = computeCoordinates({ triggerRect, contentRect, placement, align, width })
    let finalPlacement = placement
    let finalCoords = baseCoords

    // effective width for visibility check (width prop wins if provided)
    const effectiveWidth = getEffectiveWidth(width, contentRect.width)

    const placementsToTry = [
      placement,
      ((placement === "top") ? "bottom" : "top"),
      ((placement === "bottom") ? "top" : "bottom"),
      ((placement === "left") ? "right" : "left"),
      ((placement === "right") ? "left" : "right"),
    ].filter((possiblePlacement, idx, arr) => arr.indexOf(possiblePlacement) === idx) // unique

    for (const possiblePlacement of placementsToTry) {
      const candidateCoords = computeCoordinates({ triggerRect, contentRect, placement: possiblePlacement, align, width })

      const viewportRect = {
        top: (candidateCoords.top - (window.scrollY || window.pageYOffset || 0)),
        left: (candidateCoords.left - (window.scrollX || window.pageXOffset || 0)),
        right: (candidateCoords.left - (window.scrollX || window.pageXOffset || 0) + effectiveWidth),
        bottom: (candidateCoords.top - (window.scrollY || window.pageYOffset || 0) + contentRect.height),
      };

      if (isRectFullyVisible(viewportRect)) {
        finalPlacement = possiblePlacement
        finalCoords = candidateCoords
        break;
      }
    }

    // Clamp final position so the popover never overflows the viewport
    // (important in narrow iframe / plugin contexts)
    const scrollX = (window.scrollX || window.pageXOffset || 0)
    const scrollY = (window.scrollY || window.pageYOffset || 0)
    const vw = window.innerWidth
    const vh = window.innerHeight
    const effectiveW = getEffectiveWidth(width, contentRect.width)

    const clampedLeft = Math.min(
      Math.max(finalCoords.left, scrollX),                        // don't go past left edge
      scrollX + vw - effectiveW                                   // don't go past right edge
    )
    const clampedTop = Math.min(
      Math.max(finalCoords.top, scrollY),                         // don't go past top edge
      scrollY + vh - contentRect.height                           // don't go past bottom edge
    )
    const clampedCoords = { top: clampedTop, left: clampedLeft }

    setCalculatedPlacement(finalPlacement)
    setCoords(clampedCoords)

    // Recalculate arrow using the clamped content position so it still points at the trigger.
    // NOTE: DOMRect properties are non-enumerable — spread { ...contentRect } loses width/height.
    // We must explicitly copy the values we need.
    const clampedContentRect = {
      width: contentRect.width,
      height: contentRect.height,
      left: clampedLeft - scrollX,
      top: clampedTop - scrollY,
    }
    const arrowPos = getArrowPosition({ triggerRect, contentRect: clampedContentRect, placement: finalPlacement })
    setArrowPosition(arrowPos)

    // have included content in case content size changes   
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, placement, content, align])

  // debounce handler for cleaner effect
  const createDebouncedReposition = useCallback(() => {
    let debounceTimeout
    return () => {
      if (debounceTimeout) clearTimeout(debounceTimeout)
      debounceTimeout = setTimeout(() => {
        repositionPopover()
      }, 16) // ~60fps throttling
    }
  }, [repositionPopover])

  // auto close popover when trigger element becomes hidden
  useEffect(() => {
    if (!isVisible) return

    const triggerEl = popoverRef.current
    if (!triggerEl) return

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        setIsVisible(false)
      }
    }, { threshold: 0 })

    intersectionObserver.observe(triggerEl)

    return () => intersectionObserver.disconnect()
  }, [isVisible])

  //recalculating on window resize and scroll
  useEffect(() => {
    if (!isVisible) return;

    const handleReposition = createDebouncedReposition()

    window.addEventListener("resize", handleReposition)
    window.addEventListener("scroll", handleReposition, true)

    return () => {
      window.removeEventListener("resize", handleReposition)
      window.removeEventListener("scroll", handleReposition, true)
    };
  }, [isVisible, createDebouncedReposition])

  // positioning effect – runs whenever popover becomes visible
  useLayoutEffect(() => {
    if (!isVisible || !popoverContentRef.current) return

    repositionPopover()
    
    // calling again after a frame to ensure arrow content is fully rendered
    const frameId = requestAnimationFrame(() => {
      repositionPopover()
    })

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [repositionPopover, isVisible])

  return (
    <div
      ref={popoverRef}
      className={`bimmatch-popover bimmatch-popover-${theme} ${className || ""}`}
      onClick={onClickParent}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
      {content &&
        isVisible &&
        createPortal(
          <div
            ref={popoverContentRef}
            onMouseEnter={isHoverInteractive ? clearHoverTimers : undefined}
            onMouseLeave={isHoverInteractive ? onMouseLeave : undefined}
            className={`popover-content popover-content-${theme} ${"popover-content-" + calculatedPlacement} ${hideArrow ? "popover-arrow-hidden" : "popover-arrow-visible"} popover-content-${className || "defaultclass"}`}
            style={{
              position: "absolute",
              top: coords.top,
              left: coords.left,
              width: resolvedWidth
            }}
          >
            {content}
            {!hideArrow && <span className="popover-arrow" style={arrowPosition} />}
          </div>
          ,
          document.body,
          popoverId
        )}
    </div>
  );
};


// width logic
function getEffectiveWidth(width, contentWidth) {
  if (typeof width === "number") return width;
  if (width) {
    const parsed = parseFloat(width)
    return (Number.isFinite(parsed) ? parsed : contentWidth)
  }
  return contentWidth
}

// check if rect is fully visible in viewport
const isRectFullyVisible = (rect) => {
  const vw = window.innerWidth
  const vh = window.innerHeight

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.right <= vw &&
    rect.bottom <= vh
  )
}

// computing coords for a given placement which is children
const computeCoordinates = ({ triggerRect, contentRect, placement, align = "center", width }) => {
  const scrollX = (window.scrollX || window.pageXOffset || 0)
  const scrollY = (window.scrollY || window.pageYOffset || 0)

  // center
  const triggerCenterX = (triggerRect.left + (triggerRect.width / 2))
  const triggerCenterY = (triggerRect.top + (triggerRect.height / 2))

  const offsetTopBottom = 4 // smaller vertical gap — vertical spacing is more visually prominent
  const offsetLeftRight = 8 // horizontal gap (looks fine at 8px)

  const effectiveWidth = getEffectiveWidth(width, contentRect.width)

  // alignTopBottom, alignLeftRight will helo for diff type of alignments
  // horizontal
  const alignTopBottom = {
    start: (triggerRect.left + scrollX),                                 // left edge
    center: (triggerCenterX + scrollX - effectiveWidth / 2),            // centered
    end: (triggerRect.right + scrollX - effectiveWidth),                // right edge
  }

  // vertical
  const alignLeftRight = {
    start: (triggerRect.top + scrollY),                                    // top edge
    center: (triggerCenterY + scrollY - contentRect.height / 2),           // centered
    end: (triggerRect.bottom + scrollY - contentRect.height),              // bottom aligned
  }

  // left placement (popover is left of trigger)
  const leftAlignedLeft = ((triggerRect.left + scrollX) - effectiveWidth - offsetLeftRight)

  // Right placement (popover is right of trigger)
  const rightAlignedLeft = ((triggerRect.right + scrollX) + offsetLeftRight)

  // above (top placement)
  const topAlignedTop = ((triggerRect.top + scrollY) - contentRect.height - offsetTopBottom)

  // below (bottom placement)
  const bottomAlignedTop = ((triggerRect.bottom + scrollY) + offsetTopBottom)

  // final positioing
  let top = 0
  let left = 0

  switch (placement) {
    case "top":
      top = topAlignedTop
      left = alignTopBottom[align]
      break;

    case "bottom":
      top = bottomAlignedTop
      left = alignTopBottom[align]
      break;

    case "left":
      top = alignLeftRight[align]
      left = leftAlignedLeft
      break;

    case "right":
      top = alignLeftRight[align]
      left = rightAlignedLeft
      break;

    default:
      // fallback to bottom
      top = bottomAlignedTop
      left = alignTopBottom[align]
      break;
  }

  return { top, left }
}

// calculate arrow position
// Arrow points at the trigger's centre, offset relative to the (possibly clamped) tooltip box.
const getArrowPosition = ({ triggerRect, contentRect, placement }) => {
  const BORDER_HALF = 5   // half of arrow border-width (10px box)

  switch (placement) {
    case "top":
    case "bottom": {
      // Horizontal axis: arrow left = how far trigger-centre is from tooltip left edge
      const triggerCenterX = triggerRect.left + triggerRect.width / 2
      let arrowLeft = triggerCenterX - contentRect.left - BORDER_HALF
      // Keep arrow visually inside the tooltip box
      arrowLeft = Math.min(Math.max(arrowLeft, BORDER_HALF), contentRect.width - BORDER_HALF * 3)
      return placement === "top"
        ? { left: `${arrowLeft}px`, top: "100%" }
        : { left: `${arrowLeft}px`, bottom: "100%" }
    }

    case "left":
    case "right": {
      // Vertical axis: arrow top = how far trigger-centre is from tooltip top edge
      const triggerCenterY = triggerRect.top + triggerRect.height / 2
      let arrowTop = triggerCenterY - contentRect.top - BORDER_HALF
      // Keep arrow visually inside the tooltip box
      arrowTop = Math.min(Math.max(arrowTop, BORDER_HALF), contentRect.height - BORDER_HALF * 3)
      return placement === "left"
        ? { top: `${arrowTop}px`, left: "100%" }
        : { top: `${arrowTop}px`, right: "100%" }
    }

    default:
      return {}
  }
}


export default Popover;

